<script setup lang="ts">
  import NewsSummary from '@/components/News/NewsSummary.vue'
  import { useNewsStore } from '@/store/News'
  import 'swiper/css'
  import 'swiper/css/pagination'
  import { Autoplay, Navigation, Pagination } from 'swiper/modules'
  import { Swiper, SwiperSlide } from 'swiper/vue'
  const { smAndDown } = useDisplay()
  const router = useRouter()
  const route = useRoute()
  const newsStore = useNewsStore()
  const defaultNewsType = 'all'
  const currentType = computed(() => route.params.newsType?.toString() ?? defaultNewsType)
  const currentPage = computed(() => Number(route.query.page?.toString() ?? 1))
  const yearQuery = computed(() => Number.parseInt(route.query.year?.toString() ?? '') || undefined)
  const currnetYear = ref(yearQuery.value) // computed(() => route.query.year?.toString() ?? '')

  const currentSearch = computed(() => route.query.search?.toString() ?? '')

  watch(yearQuery, (year) => {
    currnetYear.value = year
  })

  // correction for route params
  watchEffect(() => {
    if (!route.params.newsType) return router.replace({ params: { newsType: defaultNewsType } })
    if (!route.query.page) return router.replace({ query: { page: 1 } })
  })

  const mobileListCarousel = computed(() =>
    currentType.value ? newsStore.getFiltered(currentType.value).slice(0, 3) : []
  )
  const mobileListRest = computed(() =>
    filteredNews.value.filter((news) => news.id !== mobileListCarousel.value[0]?.id)
  )

  const yearList = computed(() => {
    let categoryNews = newsStore.getFiltered(currentType.value)
    if (currentSearch.value)
      categoryNews = categoryNews.filter((news) => news.name.toLowerCase().includes(currentSearch.value.toLowerCase()))

    if (!categoryNews.length) return []
    return categoryNews.reduce((acc, article) => {
      const year = new Date(article.liveDate).getFullYear()
      if (!acc.includes(year)) acc.push(year)
      return acc
    }, [] as number[])
  })

  const filteredNews = computed(() => {
    let categoryNews = newsStore.getFiltered(currentType.value)
    if (currnetYear.value)
      categoryNews = categoryNews.filter((news) => new Date(news.liveDate).getFullYear() === currnetYear.value)
    if (currentSearch.value)
      categoryNews = categoryNews.filter((news) => news.name.toLowerCase().includes(currentSearch.value.toLowerCase()))
    return categoryNews
  })

  const updateQueryString = ({
    page = currentPage.value,
    year = currnetYear.value,
  }: {
    page?: number
    year?: number | null
  }) => {
    const query: { page: number; year?: number; search?: string } = { page }
    if (year) query.year = year
    router.push({ query, params: { noScrollTop: 1 } })
  }

  watch(
    () => route.params.newsType,
    () => {
      newsStore.fetchAllNews()
    }
  )

  onMounted(() => {
    newsStore.fetchAllNews()
  })
</script>

<template>
  <CardWrapper :hide-on-mobile="true">
    <div v-ga-section-view class="page-content" data-section-name="Contents" data-section-number="2">
      <div v-if="smAndDown" class="news-carousel">
        <div class="news-carousel__frame" />
        <div class="news-carousel__items">
          <Swiper
            v-if="mobileListCarousel"
            :autoplay="{ delay: 3000, pauseOnMouseEnter: true, disableOnInteraction: false }"
            :loop="true"
            :modules="[Pagination, Navigation, Autoplay]"
            :navigation="{ prevEl: '.news-carousel__arrow-left', nextEl: '.news-carousel__arrow-right' }"
            :pagination="{ clickable: true }"
          >
            <SwiperSlide v-for="newsItem in mobileListCarousel" :key="newsItem.name">
              <NewsSummaryCarousel
                :key="`news-item-mobile-${newsItem.id}`"
                v-ga-event="{
                  sectionNumber: '2',
                  sectionName: 'Contents',
                  sectionContent: newsItem.name,
                  newsCategory: newsItem.category,
                }"
                :news="newsItem"
              />
            </SwiperSlide>

            <div class="news-carousel__arrow news-carousel__arrow-left">
              <i-local-home-banner-arrow />
            </div>
            <div class="news-carousel__arrow news-carousel__arrow-right">
              <i-local-home-banner-arrow />
            </div>
          </Swiper>
        </div>
      </div>
      <v-row class="justify-space-between align-center mb-0">
        <SelectBox
          v-model:model-value="currnetYear"
          :items="yearList"
          label="Year"
          min-width="184px"
          @update:model-value="updateQueryString({ year: $event, page: 1 })"
        >
          <template #append>
            <span class="year-filter-reset" @click="updateQueryString({ year: null, page: 1 })">RESET</span>
          </template>
        </SelectBox>
        <!-- search bar section -->
        <SearchBox class="v-col-6 v-col-md-4" :default-query="{ page: 1 }" label="News Title" />
      </v-row>

      <div v-if="!filteredNews.length && currentSearch" class="no-result my-16 text-center">
        <h3>No Results Found</h3>
      </div>
      <ViewMoreItems
        v-if="smAndDown"
        :items="mobileListRest"
        :setting="{
          initialItemsNumber: 10,
          revealNumberPerClick: 6,
        }"
      >
        <template #item="item">
          <NewsSummary
            :key="`news-item-mobile-${item.id}`"
            v-ga-event="{
              sectionContent: item.name,
              newsCategory: item.category,
            }"
            :news="item"
            :type="'v2'"
          />
        </template>
      </ViewMoreItems>
      <PaginationWithItems
        v-else
        :current-page="currentPage"
        :items="filteredNews"
        :setting="{ itemsPerPage: 18, maxPagination: 6, hash: '#filterbox' }"
        @update:current-page="updateQueryString({ page: $event })"
      >
        <template #item="item">
          <NewsSummary
            :key="`news-item-desktop-${item.id}`"
            v-ga-event="{
              sectionContent: item.name,
              newsCategory: item.category,
            }"
            :news="item"
            :type="'v2'"
          />
        </template>
      </PaginationWithItems>
    </div>
  </CardWrapper>
</template>
<style lang="scss" scoped>
  :global(:root) {
    --news-carousel-arrow-size: 5.7vw;
    --news-carousel-bullet-size-tablet: 2vw;
    --news-carousel-bullet-size-mobile: 2vw;
  }
  .filter-section {
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
  }
  .year-filter-reset {
    text-decoration: underline;
    text-underline-offset: 4px;
    color: var(--hyperlink-text-color);
    font-family: 'nexon_lv2_gothic';
    font-weight: 500;
    cursor: pointer;
    font-size: 16px;
  }

  // .page-content {
  //   min-height: 1000px;
  // }

  //  Carousel
  .news-carousel {
    position: relative;
    padding-top: 2vw;
    padding-bottom: 2vw;
    background: white;
    border-radius: 14px;
    margin-bottom: 20px;
    box-shadow: 0px 0px 5px 0 rgba(0, 0, 0, 0.2);
  }
  .news-carousel__frame {
    background: #dd9d70;
    width: 100%;
    height: 54vw;
    position: absolute;
    left: 0;
    top: 0;
    border-radius: 14px 14px 0 0;
  }

  .news-carousel__items {
    margin: 0 auto 0px;
    width: 95%;
  }

  .news-carousel__arrow {
    position: absolute;
    top: 23vw;
    z-index: 10;
    width: var(--news-carousel-arrow-size);
    height: calc(var(--news-carousel-arrow-size) * 2);
    margin-top: calc(0px - var(--news-carousel-arrow-size));
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #dd9d70;
    svg {
      width: 3vw;
      path {
        fill: black;
      }
    }
  }
  .news-carousel__arrow-left {
    left: -0.7vw;
    transform: trans;
    border-radius: 0 var(--news-carousel-arrow-size) var(--news-carousel-arrow-size) 0;
    svg {
      margin-right: 1.5vw;
    }
  }
  .news-carousel__arrow-right {
    right: -0.7vw;
    border-radius: var(--news-carousel-arrow-size) 0 0 var(--news-carousel-arrow-size);
    svg {
      margin-left: 1.5vw;
      transform: rotateY(180deg);
    }
  }

  // Desktop List
  :deep(.pagination__list-items) {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    > a {
      flex: 0 0 calc(33.33% - (2 * 24px / 3));
    }
  }

  @media #{$tablet} {
    :deep(.view-more-items) {
      display: flex;
      flex-wrap: wrap;
      gap: 24px;
      > .view-more-items__item {
        flex: 0 0 calc(50% - (12px));
      }
    }

    :deep(.swiper) {
      .swiper-pagination {
        bottom: 0;
      }
      .swiper-pagination-bullet {
        opacity: 1;
        background: #d2d2d2;
        margin: 0 0 0 1.5vw !important;
        width: var(--news-carousel-bullet-size-tablet);
        height: var(--news-carousel-bullet-size-tablet);
        position: relative;
        &.swiper-pagination-bullet-active {
          &::before {
            content: '';
            position: absolute;
            background: #8c8c8c;
            width: calc(var(--news-carousel-bullet-size-tablet) - 4px);
            height: calc(var(--news-carousel-bullet-size-tablet) - 4px);
            top: 2px;
            left: 2px;
            border-radius: 100px;
          }
        }
        &:first-child {
          margin: 0;
        }
      }
    }
  }

  @media #{$mobile} {
    :deep(.view-more-items) {
      display: block;
    }
    :deep(.swiper) {
      .swiper-pagination-bullet {
        width: var(--news-carousel-bullet-size-mobile);
        height: var(--news-carousel-bullet-size-mobile);
        &.swiper-pagination-bullet-active {
          &::before {
            width: calc(var(--news-carousel-bullet-size-mobile) - 4px);
            height: calc(var(--news-carousel-bullet-size-mobile) - 4px);
          }
        }
      }
    }
  }
</style>
